import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSigningOut, setIsSigningOut] = useState(false);
  const navigate = useNavigate();
  
  // Add authentication context
  const { user, userProfile, signOut, isAuthenticated } = useAuth();

  const notifications = [
    {
      id: 1,
      type: 'warning',
      title: 'VAT Return Due',
      message: 'Q3 VAT return due in 3 days',
      time: '2 hours ago',
      unread: true
    },
    {
      id: 2,
      type: 'success',
      title: 'Payment Received',
      message: 'Invoice #INV-2024-001 paid by Acme Corp',
      time: '4 hours ago',
      unread: true
    },
    {
      id: 3,
      type: 'info',
      title: 'AI Insight',
      message: 'Unusual expense pattern detected in office supplies',
      time: '1 day ago',
      unread: false
    }
  ];

  const quickActions = [
    { name: 'Create Invoice', icon: 'FileText', path: '/sales-hub' },
    { name: 'Record Expense', icon: 'Receipt', path: '/purchase-center' },
    { name: 'Add Customer', icon: 'UserPlus', path: '/sales-hub' },
    { name: 'Record Payment', icon: 'CreditCard', path: '/banking' }
  ];

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Searching for:', searchQuery);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      setIsSearchOpen(false);
      setIsNotificationOpen(false);
      setIsProfileOpen(false);
    }
    if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
      e?.preventDefault();
      setIsSearchOpen(true);
    }
  };

  const handleProfileClick = () => {
    setIsProfileOpen(false);
    navigate('/profile-settings');
  };

  const handleAccountClick = () => {
    setIsProfileOpen(false);
    navigate('/organization-settings');
  };

  const handleHelpClick = () => {
    setIsProfileOpen(false);
    // Could navigate to help page or show help modal
    console.log('Help & Support clicked');
  };

  const handleSignOut = async () => {
    if (isSigningOut) return; // Prevent multiple calls
    
    setIsSigningOut(true);
    setIsProfileOpen(false);
    
    try {
      console.log('Signing out...');
      const { error } = await signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        // Still navigate even if there's an error to ensure user gets signed out locally
      } else {
        console.log('Sign out successful');
      }
      
      // Navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Sign out exception:', error);
      // Navigate anyway to ensure user experience
      navigate('/login', { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const unreadCount = notifications?.filter(n => n?.unread)?.length;

  // Get display name and email from auth context
  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const userEmail = user?.email || userProfile?.email || '';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border shadow-professional">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">AccounTech AI</span>
            </div>
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-4">
            {/* Search Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchOpen(true)}
              className="relative"
            >
              <Icon name="Search" size={20} />
            </Button>

            {/* Quick Actions */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative"
              >
                <Icon name="Plus" size={20} />
              </Button>
            </div>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {/* Notifications Dropdown */}
              {isNotificationOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-elevated z-dropdown">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-medium text-foreground">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications?.map((notification) => (
                      <div
                        key={notification?.id}
                        className={`p-4 border-b border-border hover:bg-muted/50 cursor-pointer ${
                          notification?.unread ? 'bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            notification?.type === 'warning' ? 'bg-warning' :
                            notification?.type === 'success'? 'bg-success' : 'bg-primary'
                          }`} />
                          <div className="flex-1">
                            <p className="font-medium text-sm text-foreground">{notification?.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">{notification?.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">{notification?.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-border">
                    <Button variant="ghost" size="sm" className="w-full">
                      View All Notifications
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 px-3"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <span className="text-sm font-medium text-foreground hidden md:block">
                  {displayName}
                </span>
                <Icon name="ChevronDown" size={16} />
              </Button>

              {/* Profile Dropdown */}
              {isProfileOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevated z-dropdown">
                  <div className="p-4 border-b border-border">
                    <p className="font-medium text-foreground">{displayName}</p>
                    <p className="text-sm text-muted-foreground">{userEmail}</p>
                  </div>
                  <div className="py-2">
                    <button 
                      onClick={handleProfileClick}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 flex items-center space-x-2 transition-colors"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button 
                      onClick={handleAccountClick}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 flex items-center space-x-2 transition-colors"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Account Settings</span>
                    </button>
                    <button 
                      onClick={handleHelpClick}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 flex items-center space-x-2 transition-colors"
                    >
                      <Icon name="HelpCircle" size={16} />
                      <span>Help & Support</span>
                    </button>
                  </div>
                  <div className="border-t border-border py-2">
                    <button 
                      onClick={handleSignOut}
                      disabled={isSigningOut}
                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-muted/50 flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name={isSigningOut ? "Loader2" : "LogOut"} size={16} className={isSigningOut ? "animate-spin" : ""} />
                      <span>{isSigningOut ? 'Signing Out...' : 'Sign Out'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/50 z-search flex items-start justify-center pt-20">
          <div className="bg-popover border border-border rounded-lg shadow-floating w-full max-w-2xl mx-4">
            <form onSubmit={handleSearch} className="p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Search" size={20} className="text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search transactions, customers, invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground"
                  autoFocus
                />
                <kbd className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">ESC</kbd>
              </div>
            </form>
            {searchQuery && (
              <div className="border-t border-border p-4">
                <p className="text-sm text-muted-foreground">Press Enter to search for "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Click Outside Handler */}
      {(isNotificationOpen || isProfileOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsNotificationOpen(false);
            setIsProfileOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Header;